import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

export default class extends BridgeComponent {
  static component = "nfc_scanner"
  static targets = ["status", "result", "error"]

    // Add method to handle messages from native side
  onReceive(message) {
    console.log("=== Received message from native ===")
    console.log("Message:", message)
    console.log("Event:", message.event)
    console.log("Data:", message.data)

    super.onReceive(message)
  }

  testBridgeDirectly() {
    console.log('=== Testing Bridge Directly ===')
    console.log('window.webkit:', window.webkit)
    console.log('window.webkit.messageHandlers:', window.webkit?.messageHandlers)
    console.log('window.webkit.messageHandlers.bridge:', window.webkit?.messageHandlers?.bridge)
    console.log('window.nativeBridge:', window.nativeBridge)
    console.log('window.HotwireNative:', window.HotwireNative)
    console.log('window.Strada:', window.Strada)

    if (window.webkit?.messageHandlers?.bridge) {
      console.log('Direct webkit bridge available - trying manual message')
      try {
        window.webkit.messageHandlers.bridge.postMessage({
          component: "nfc_scanner",
          event: "test",
          id: "test-123",
          data: {}
        })
        console.log('Manual bridge message sent')
      } catch (error) {
        console.error('Manual bridge message failed:', error)
      }
    }
  }

    // Manual bridge detection as fallback
  get isBridgeAvailable() {
    // Check multiple indicators of bridge availability
    if (this.bridgeSupported === true) return true
    if (this.bridgeSupported === false) return false

    // Fallback checks for iOS app
    const hasWebkit = !!(window.webkit && window.webkit.messageHandlers)
    const hasHotwire = !!(window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.hotwire)
    const hasStrada = !!(window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.strada)

    // Check user agent for Hotwire Native indicators
    const userAgent = navigator.userAgent || ''
    const isHotwireNative = userAgent.includes('Hotwire Native') || userAgent.includes('Turbo Native')

    return hasHotwire || hasStrada || isHotwireNative
  }

    connect() {
    console.log('=== NFC Scanner Controller Connected ===')
    console.log('Bridge supported:', this.bridgeSupported)
    console.log('Send method exists:', typeof this.send === 'function')

    super.connect()

    // Ensure button starts in enabled state
    this.resetButton()

    // Test bridge communication directly
    this.testBridgeDirectly()

    // Wait a moment for bridge to initialize, then check support
    setTimeout(() => {
      console.log('=== Bridge Debug Info ===')
      console.log('Bridge supported (property):', this.bridgeSupported)
      console.log('Bridge available (computed):', this.isBridgeAvailable)
      console.log('Window webkit:', window.webkit)
      console.log('Component properties:', Object.getOwnPropertyNames(this))
      console.log('Component prototype:', Object.getOwnPropertyNames(Object.getPrototypeOf(this)))
      console.log('Element:', this.element)
      console.log('=== End Debug Info ===')

      // Check if we're running in a native iOS app
      if (this.isBridgeAvailable) {
        console.log('Bridge is available - NFC scanning should work')
      } else {
        console.warn('Bridge not available - running in web browser')
        console.log('bridgeSupported value:', this.bridgeSupported)
        this.showWebBrowserMessage()
      }
    }, 100)
  }

  disconnect() {
    console.log('=== NFC Scanner Controller Disconnected ===')
    // Reset button state when controller is disconnected
    if (this.element) {
      this.resetButton()
    }
    super.disconnect()
  }

  showWebBrowserMessage() {
    const userAgent = navigator.userAgent || ''
    const isMobileSafari = /Safari/.test(userAgent) && /Mobile/.test(userAgent) && !/Chrome/.test(userAgent)
    const isIOS = /iPad|iPhone|iPod/.test(userAgent)

    let message = `
      <div class="space-y-2">
        <p class="font-semibold">📱 NFC scanning requires the iOS app</p>
    `

    if (isIOS && isMobileSafari) {
      message += `
        <p class="text-sm text-orange-600">You're using Safari on iOS. To scan NFC tags:</p>
        <ol class="text-sm list-decimal list-inside space-y-1 mt-2">
          <li>Install the Hotwire Native iOS app</li>
          <li>Open this URL in the app (not Safari)</li>
          <li>Use the NFC scanner feature</li>
        </ol>
      `
    } else {
      message += `
        <p class="text-sm">You're currently using a web browser. To scan NFC tags:</p>
        <ol class="text-sm list-decimal list-inside space-y-1 mt-2">
          <li>Install the iOS app from the App Store</li>
          <li>Open this page in the app</li>
          <li>Use the NFC scanner feature</li>
        </ol>
      `
    }

    message += `
        <p class="text-xs text-gray-500 mt-2">
          Alternative: Use the <a href="/qr_scanners/new" class="text-blue-600 underline">QR Scanner</a> which works in browsers
        </p>
        <div class="text-xs text-gray-400 mt-2 p-2 bg-gray-50 rounded">
          Debug: ${userAgent.substring(0, 100)}...
        </div>
      </div>
    `

    this.showError(message)
    this.element.disabled = true
    this.element.textContent = 'NFC Not Available - Need iOS App'
    this.element.classList.add('opacity-50')
  }





    async startScan(event) {
    console.log('=== startScan called ===')
    console.log('Bridge available:', this.isBridgeAvailable)

    // Prevent default if it's a form submission
    if (event) {
      event.preventDefault()
    }

    this.clearMessages()

    // Check bridge availability before attempting scan
    if (!this.isBridgeAvailable) {
      this.showWebBrowserMessage()
      return
    }

    this.showStatus('Preparing to scan...')
    this.element.disabled = true
    this.element.textContent = 'Scanning...'
    this.element.classList.add('opacity-50')

    // Store the timeout reference so we can clear it if needed
    let scanTimeout = null

    try {
      console.log('Sending startScan message to bridge...')
      console.log('Bridge available:', this.isBridgeAvailable)

      // The send method uses callbacks, not Promises!
      console.log('Sending startScan with callback...')

            // Create a Promise wrapper around the callback-based send method
      const result = await new Promise((resolve, reject) => {
        scanTimeout = setTimeout(() => {
          console.log('NFC scan timed out after 45 seconds')
          reject(new Error('NFC scan timeout after 45 seconds'))
        }, 45000)

        // Additional safety timeout specifically for cancellation scenarios
        const cancellationTimeout = setTimeout(() => {
          console.log('NFC scan appears to be cancelled but no response received - forcing button reset')
          reject(new Error('NFC scan was likely cancelled'))
        }, 2000) // Short timeout to detect quick cancellations

        this.send("startScan", {}, (message) => {
          if (scanTimeout) {
            clearTimeout(scanTimeout)
            scanTimeout = null
          }
          if (cancellationTimeout) {
            clearTimeout(cancellationTimeout)
          }
          console.log('Callback received message:', message)

          // Handle both success and cancellation cases
          if (message && message.data) {
            // Check if the data contains cancellation info
            const data = typeof message.data === 'string' ? JSON.parse(message.data) : message.data
            if (data && data.cancelled) {
              reject(new Error(data.message || 'NFC scan was cancelled by user'))
            } else {
              resolve(message.data)
            }
          } else if (message && message.error) {
            reject(new Error(message.error))
          } else if (message && message.cancelled) {
            reject(new Error('NFC scan was cancelled by user'))
          } else {
            resolve(message.data) // Fallback for legacy compatibility
          }
        })
      })

      console.log('Received result from callback:', result)
      this.handleScanResponse(result)

    } catch (error) {
      console.error('Error in startScan:', error)

      // Clear timeout if it's still active
      if (scanTimeout) {
        clearTimeout(scanTimeout)
        scanTimeout = null
      }

      // Show appropriate error message based on error type
      if (error.message.includes('cancelled')) {
        this.showError('NFC scan was cancelled')
      } else if (error.message.includes('timeout')) {
        this.showError('NFC scan timed out. Please try again.')
      } else {
        this.showError(`Failed to start NFC scan: ${error.message}`)
      }

      // Always reset button state on any error
      this.resetButton()
    } finally {
      // Ensure button is always re-enabled, even if something unexpected happens
      setTimeout(() => {
        if (this.element.disabled) {
          console.log('Force-enabling button after scan attempt')
          this.resetButton()
        }
      }, 1000) // Small delay to allow normal flow to complete first
    }
  }

    // Handle the response from the startScan bridge message
  handleScanResponse(result) {
    try {
      console.log("NFC Scan response from iOS:", result)
      console.log("NFC Scan response type:", typeof result)

      // Check if result is null or undefined
      if (result === null || result === undefined) {
        this.showError(`
          <div class="space-y-2">
            <p class="font-semibold">⚠️ No response from iOS NFC component</p>
            <p class="text-sm">Possible causes:</p>
            <ul class="text-sm list-disc list-inside space-y-1">
              <li>NFC is not available on this device</li>
              <li>NFC permissions are not granted</li>
              <li>iOS NFC session failed to start</li>
              <li>iOS component needs to be updated</li>
            </ul>
            <p class="text-xs text-gray-500 mt-2">
              Check the Xcode console for iOS-side error messages.
            </p>
          </div>
        `)
        return
      }

      // Parse JSON string if needed
      let parsedData = result
      if (typeof result === 'string') {
        try {
          parsedData = JSON.parse(result)
        } catch (parseError) {
          console.error('JSON parse error:', parseError)
          this.showError(`Invalid response format: ${parseError.message}`)
          return
        }
      }

      // Additional safety check
      if (!parsedData || typeof parsedData !== 'object') {
        this.showError('Invalid response data format')
        return
      }

      // Check if it's an error response
      if (parsedData.error) {
        this.showError(parsedData.message || 'Failed to scan NFC tag')
      } else if (parsedData.success) {
        // Process the successful NFC data
        const nfcData = this.processNativeNFCData(parsedData)
        this.showResult(`NFC tag read successfully!`)

        // Send data to Rails controller
        this.sendToController(nfcData)
      } else {
        this.showError('Unknown response format from NFC scanner')
      }

    } catch (error) {
      console.error('Error in handleScanResponse:', error)
      this.showError(`Error processing NFC response: ${error.message}`)
    } finally {
      // Always reset button state, regardless of success or failure
      this.resetButton()
      console.log('Button reset after handling scan response')
    }
  }

  // Legacy method - kept for compatibility but may not be used with new flow
  handleScanResult(data) {
    console.log("Legacy handleScanResult called:", data)
    this.handleScanResponse(data)
  }

  // Legacy method - kept for compatibility but may not be used with new flow
  handleScanError(errorData) {
    console.error("Legacy handleScanError called:", errorData)
    this.showError(errorData.message || 'Failed to scan NFC tag')
    // Always reset button on error
    this.resetButton()
    console.log('Button reset after legacy error handling')
  }

  processNativeNFCData(data) {
    // Process the data format sent from iOS
    // This will depend on how the iOS app structures the NFC data
    return {
      serialNumber: data.serialNumber || data.identifier,
      records: data.records || [
        {
          recordType: data.type || "text",
          data: data.payload || data.data || data.content
        }
      ],
      rawData: data
    }
  }

  async sendToController(nfcData) {
    try {
      const response = await fetch('/scanners', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ nfc_data: nfcData })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      // this.showResult(`Data sent successfully: ${JSON.stringify(result)}`)
      this.showResult("NFC tag read successfully!")

    } catch (error) {
      this.showError(`Failed to send data: ${error.message}`)
    }
  }

  showStatus(message) {
    this.clearMessages()
    const statusElement = document.getElementById('nfc-status')
    statusElement.textContent = message
    statusElement.classList.remove('hidden')
  }

  showResult(message) {
    this.clearMessages()
    const resultElement = document.getElementById('nfc-result')
    resultElement.textContent = message
    resultElement.classList.remove('hidden')
  }

  showError(message) {
    this.clearMessages()
    const errorElement = document.getElementById('nfc-error')
    // Support both text and HTML content
    if (message.includes('<')) {
      errorElement.innerHTML = message
    } else {
      errorElement.textContent = message
    }
    errorElement.classList.remove('hidden')
  }

  clearMessages() {
    document.getElementById('nfc-status').classList.add('hidden')
    document.getElementById('nfc-result').classList.add('hidden')
    document.getElementById('nfc-error').classList.add('hidden')
  }

  resetButton() {
    this.element.disabled = false
    this.element.textContent = 'Scan NFC Tag'

    // Clear any status messages when resetting
    this.clearMessages()

    // Add a subtle visual feedback that button is ready
    this.element.classList.remove('opacity-50')
    console.log('NFC button reset and ready for next scan')
  }
}
